// Order API Service
// API client for order operations

import type {
  OrderResponse,
  OrdersListResponse,
  CreateOrderRequest,
} from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://bonbalayelo-api-preview.bonbalayelo.workers.dev';

/**
 * Helper function to get auth token
 */
function getAuthToken(): string | null {
  return localStorage.getItem('auth_token');
}

/**
 * Helper function to make authenticated API calls
 */
async function authenticatedFetch<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const token = getAuthToken();

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Unknown error' } }));
    throw new Error(error.error?.message || 'API request failed');
  }

  return response.json();
}

/**
 * Generate idempotency key for order creation
 * Uses UUID v4 format
 */
function generateIdempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * Order API Service
 */
export const orderApi = {
  /**
   * Get user's orders (paginated)
   * @param options - Query options (limit, offset, status)
   */
  async getOrders(options?: {
    limit?: number;
    offset?: number;
    status?: string;
  }): Promise<OrdersListResponse> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.status) params.append('status', options.status);

    const queryString = params.toString();
    const endpoint = `/api/orders${queryString ? `?${queryString}` : ''}`;

    return authenticatedFetch<OrdersListResponse>(endpoint, {
      method: 'GET',
    });
  },

  /**
   * Get order details by ID
   * @param orderId - Order ID
   */
  async getOrderById(orderId: string): Promise<OrderResponse> {
    return authenticatedFetch<OrderResponse>(`/api/orders/${orderId}`, {
      method: 'GET',
    });
  },

  /**
   * Create order from cart
   * Automatically generates idempotency key if not provided
   * @param request - Create order request
   */
  async createOrder(request: CreateOrderRequest): Promise<OrderResponse> {
    // Auto-generate idempotency key if not provided
    const orderRequest = {
      ...request,
      idempotencyKey: request.idempotencyKey || generateIdempotencyKey(),
    };

    return authenticatedFetch<OrderResponse>('/api/orders', {
      method: 'POST',
      body: JSON.stringify(orderRequest),
    });
  },

  /**
   * Cancel order
   * @param orderId - Order ID to cancel
   * @param reason - Optional cancellation reason
   */
  async cancelOrder(orderId: string, reason?: string): Promise<OrderResponse> {
    return authenticatedFetch<OrderResponse>(`/api/orders/${orderId}/cancel`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    });
  },
};
